/**
 * StrategyBuilderPage
 *
 * Page for creating and managing AlgoBuilderX strategies.
 * Features:
 * - Strategy list view
 * - Create new strategy via wizard
 * - Strategy management (start/stop/delete)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAuth } from '../provider/authProvider';
import { useAccount } from '../provider/accountProvider';
import { AlgoBuilderXWizard } from '../Components/AlgoBuilderX';
import StrategyEditDialog from '../Components/AlgoBuilderX/StrategyEditDialog';
import {
  getStrategies,
  startStrategy,
  stopStrategy,
  deleteStrategy
} from '../api/algoBuilderXApi';

const BRAND = {
  background: '#0A0A0F',
  surface: '#14141A',
  surfaceLight: '#1a1a2e',
  text: '#FFFFFF',
  textSecondary: '#E2E8F0',
  textMuted: '#94A3B8',
  primary: '#27B56D',
  primaryLight: '#22C55E',
  blue: '#2563eb',
  border: 'rgba(255, 255, 255, 0.12)',
  error: '#EF4444'
};

const StrategyBuilderPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { getAccountNumber } = useAccount();

  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  // Fetch strategies
  const fetchStrategies = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getStrategies(token);
      setStrategies(data || []);
    } catch (err) {
      console.error('[StrategyBuilder] Fetch failed:', err);
      setError('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  // Strategy actions
  const handleStartStrategy = async (strategyId) => {
    try {
      setActionLoading(prev => ({ ...prev, [strategyId]: 'start' }));
      await startStrategy(token, strategyId);
      toast.success('Strategy started');
      fetchStrategies();
    } catch (err) {
      toast.error(err.message || 'Failed to start strategy');
    } finally {
      setActionLoading(prev => ({ ...prev, [strategyId]: null }));
    }
  };

  const handleStopStrategy = async (strategyId) => {
    try {
      setActionLoading(prev => ({ ...prev, [strategyId]: 'stop' }));
      await stopStrategy(token, strategyId);
      toast.success('Strategy stopped');
      fetchStrategies();
    } catch (err) {
      toast.error(err.message || 'Failed to stop strategy');
    } finally {
      setActionLoading(prev => ({ ...prev, [strategyId]: null }));
    }
  };

  const handleDeleteStrategy = async (strategyId, strategyName) => {
    if (!window.confirm(`Delete strategy "${strategyName}"? This cannot be undone.`)) {
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [strategyId]: 'delete' }));
      await deleteStrategy(token, strategyId);
      toast.success('Strategy deleted');
      fetchStrategies();
    } catch (err) {
      toast.error(err.message || 'Failed to delete strategy');
    } finally {
      setActionLoading(prev => ({ ...prev, [strategyId]: null }));
    }
  };

  // On wizard save
  const handleWizardSave = (newStrategy) => {
    fetchStrategies();
    setWizardOpen(false);
  };

  // Get status chip
  const getStatusChip = (strategy) => {
    const isRunning = strategy.isRunning || strategy.status?.toLowerCase() === 'running';
    return (
      <Chip
        label={isRunning ? 'Running' : 'Stopped'}
        size="small"
        sx={{
          backgroundColor: isRunning ? 'rgba(39, 181, 109, 0.15)' : 'rgba(255, 255, 255, 0.1)',
          color: isRunning ? BRAND.primary : BRAND.textMuted,
          fontWeight: 600,
          fontSize: '0.75rem'
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: BRAND.background,
        minHeight: '100vh',
        py: 3
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton
            onClick={() => navigate('/dashboard')}
            sx={{ color: BRAND.textMuted, mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ color: BRAND.text, fontWeight: 700 }}>
              Strategy Builder
            </Typography>
            <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem' }}>
              Create and manage your AlgoBuilderX trading strategies
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchStrategies}
                disabled={loading}
                sx={{ color: BRAND.textMuted }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setWizardOpen(true)}
              sx={{
                backgroundColor: BRAND.primary,
                '&:hover': { backgroundColor: BRAND.primaryLight },
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Create Strategy
            </Button>
          </Box>
        </Box>

        {/* Error */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {!loading && strategies.length === 0 && (
          <Card
            sx={{
              backgroundColor: BRAND.surfaceLight,
              border: `1px solid ${BRAND.border}`,
              borderRadius: '12px',
              textAlign: 'center',
              py: 8
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ color: BRAND.text, mb: 2 }}>
                No Strategies Yet
              </Typography>
              <Typography sx={{ color: BRAND.textMuted, mb: 3 }}>
                Create your first trading strategy using the Strategy Builder wizard.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setWizardOpen(true)}
                sx={{
                  backgroundColor: BRAND.primary,
                  '&:hover': { backgroundColor: BRAND.primaryLight },
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Create Your First Strategy
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Strategies Grid */}
        {!loading && strategies.length > 0 && (
          <Grid container spacing={3}>
            {strategies.map((strategy) => {
              const strategyId = strategy.id || strategy.strategyId;
              const isRunning = strategy.isRunning || strategy.status?.toLowerCase() === 'running';
              const currentAction = actionLoading[strategyId];

              return (
                <Grid item xs={12} md={6} lg={4} key={strategyId}>
                  <Card
                    sx={{
                      backgroundColor: BRAND.surfaceLight,
                      border: `1px solid ${BRAND.border}`,
                      borderRadius: '12px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <CardContent sx={{ flex: 1 }}>
                      {/* Title & Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: BRAND.text,
                            fontWeight: 600,
                            flex: 1,
                            mr: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {strategy.name}
                        </Typography>
                        {getStatusChip(strategy)}
                      </Box>

                      {/* Description */}
                      {strategy.description && (
                        <Typography
                          sx={{
                            color: BRAND.textMuted,
                            fontSize: '0.875rem',
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {strategy.description}
                        </Typography>
                      )}

                      {/* Components Info */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {strategy.components?.indicator && (
                          <Chip
                            label={`${Array.isArray(strategy.components.indicator) ? strategy.components.indicator.length : 1} Indicator(s)`}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(139, 92, 246, 0.15)',
                              color: '#8b5cf6',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                        {strategy.components?.entryRule && (
                          <Chip
                            label="Entry"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(34, 197, 94, 0.15)',
                              color: '#22c55e',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                        {strategy.components?.exitRule && (
                          <Chip
                            label="Exit"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(239, 68, 68, 0.15)',
                              color: '#ef4444',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>

                    {/* Actions */}
                    <Box
                      sx={{
                        borderTop: `1px solid ${BRAND.border}`,
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1
                      }}
                    >
                      <Tooltip title="View/Edit">
                        <IconButton
                          onClick={() => {
                            setSelectedStrategyId(strategyId);
                            setEditDialogOpen(true);
                          }}
                          disabled={!!currentAction}
                          sx={{ color: BRAND.blue }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {isRunning ? (
                        <Tooltip title="Stop Strategy">
                          <IconButton
                            onClick={() => handleStopStrategy(strategyId)}
                            disabled={!!currentAction}
                            sx={{ color: BRAND.error }}
                          >
                            {currentAction === 'stop' ? (
                              <CircularProgress size={20} />
                            ) : (
                              <StopIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Start Strategy">
                          <IconButton
                            onClick={() => handleStartStrategy(strategyId)}
                            disabled={!!currentAction}
                            sx={{ color: BRAND.primary }}
                          >
                            {currentAction === 'start' ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PlayIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Strategy">
                        <IconButton
                          onClick={() => handleDeleteStrategy(strategyId, strategy.name)}
                          disabled={!!currentAction || isRunning}
                          sx={{ color: BRAND.textMuted, '&:hover': { color: BRAND.error } }}
                        >
                          {currentAction === 'delete' ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* Strategy Wizard Dialog */}
      <AlgoBuilderXWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSave={handleWizardSave}
        token={token}
        accountNumber={getAccountNumber()}
      />

      {/* Strategy Edit Dialog */}
      <StrategyEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedStrategyId(null);
        }}
        strategyId={selectedStrategyId}
        token={token}
        onSave={fetchStrategies}
      />
    </Box>
  );
};

export default StrategyBuilderPage;
